import {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import useAsyncEffect from 'use-async-effect';

import { SCREENS } from '@core/assistant/interfaces';
import { debugEventId } from '@core/assistant/constants';
import { PollyLanguage } from '@shared/interfaces/polly';
import { IEventSettingsAiAssistant } from '@interfaces/event';
import { TriggerType } from '@interfaces/assistantScript';
import { teleportToRegion as teleportToRegionFn } from './providerV1';

import { IAssistantContext, ProviderProps } from './interfaces';
import { initDebugSendFn, log } from './Debug';
import { globals } from './globals';
import { LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { resolveJoinTriggerName, buildParentTriggerMap } from './providerUtils';

import { useAssistantNavigation } from './useAssistantNavigation';
import { useAssistantChat, GeneratePollyAudioFn } from './useAssistantChat';
import { useAssistantProgress } from './useAssistantProgress';
import {
  useAssistantScripts,
  GetEffectiveAiSettingsFn,
} from './useAssistantScripts';
import {
  useAssistantAudio,
  ScriptTransitionCallbacks,
} from './useAssistantAudio';
import {
  useScriptFlow,
  PlayBlockAudioFn,
  CbOnCompletedStepFn,
  StartAudioPrerenderFn,
  StartFlowFn,
} from './useScriptFlow';
import { useRegionHandler } from './useRegionHandler';

export const AssistantContext = createContext<IAssistantContext>(
  {} as IAssistantContext,
);

export const useAssistant = () => useContext(AssistantContext);

const defaultAvailableLanguages: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.FRENCH,
  PollyLanguage.ITALIAN,
  PollyLanguage.GERMAN,
  PollyLanguage.ARABIC,
  PollyLanguage.CHINESE,
  PollyLanguage.SPANISH,
];

const defaultAiSettings: IEventSettingsAiAssistant = {
  engine: 'generative',
  voiceId: 'Ruth',
  language: PollyLanguage.ENGLISH,
};

const AssistantProviderInner: FC<ProviderProps> = ({
  children,
  currentRegionName,
  userName,
  mytaverseEventRef,
  sendWSRef,
  selectedLanguageRef,
  mytaverseUserRef,
  objectPoiId,
  assistantScriptUpdateTick,
  activeBrandingId,
  assistantSound,
  pendingAssistantRegionTrigger,
}) => {
  const [open, setOpen] = useState(false);
  const [spotTriggerType, setSpotTriggerType] = useState<TriggerType | null>(
    null,
  );
  const [isSpotImageModalOpen, setIsSpotImageModalOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isMuted, setIsMutedLoc] = useState(false);
  const [audioLanguage, setAudioLanguage] = useState<PollyLanguage>(
    PollyLanguage.ENGLISH,
  );

  const [audioPromptHistory] = useState<string[]>([]);

  useEffect(() => {
    const gain = assistantSound ?? 1;
    globals.assistantGain = gain;
    try {
      globals.visualizerInstance?.setGain(gain);
    } catch {}
  }, [assistantSound]);

  const sendJSONMessageToWebSocketRef = sendWSRef;

  const openRef = useRef(open);
  openRef.current = open;

  const setOpenAuto = useCallback((value: boolean) => {
    if (value && mytaverseEventRef.current?.isBrandingModeEnabled) {
      return;
    }
    setOpen(value);
  }, []);

  const prevOpenRef = useRef(open);

  const userNameRef = useRef(userName);
  userNameRef.current = userName;

  const activeBrandingIdRef = useRef(activeBrandingId);
  activeBrandingIdRef.current = activeBrandingId;

  // The real "on join" region (a map region flagged isStartingPoint), falling
  // back to the synthetic 'landing' pseudo-trigger for maps without one.
  // Recomputed every render since mytaverseEventRef is a ref (no re-render on
  // mutation) — cheap, just a scan over the current map's region list.
  const joinTriggerName = resolveJoinTriggerName(
    mytaverseEventRef.current?.currentRoom?.map?.regions,
  );
  const joinTriggerNameRef = useRef(joinTriggerName);
  joinTriggerNameRef.current = joinTriggerName;

  // Child region (e.g. an exhibition booth) -> parent region name, for
  // scripts bound to the parent only. Recomputed every render for the same
  // reason as joinTriggerName above.
  const parentTriggerMap = buildParentTriggerMap(
    mytaverseEventRef.current?.currentRoom?.map?.regions,
  );
  const parentTriggerMapRef = useRef(parentTriggerMap);
  parentTriggerMapRef.current = parentTriggerMap;

  const prevActiveBrandingIdRef = useRef(activeBrandingId);

  const debugEventAiSettingsRef = useRef<IEventSettingsAiAssistant | null>(
    null,
  );

  // Shared forward-refs — resolved after hook calls below
  const linearScriptPlayerRef = useRef<LinearScriptPlayer | undefined>(
    undefined,
  );
  const enqueuedScriptIdRef = useRef<string | null>(null);
  // Set when the user leaves into an empty region while a finish_step script
  // with bridgeRegionGaps is still playing. The stop/UI-reset is deferred until
  // the current step's audio ends naturally (handled in useAssistantAudio).
  const pendingRegionClearRef = useRef<boolean>(false);
  const startFlowRef = useRef<StartFlowFn>(() => {});
  const stopPlaybackRef = useRef<() => void>(() => {});
  const preRenderedAudioRef = useRef<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >([]);
  const resetAssistantUIRef = useRef<() => void>(() => {});
  const generatePollyAudioRef = useRef<GeneratePollyAudioFn>(async () => {});
  const cbOnCompletedStepRef = useRef<CbOnCompletedStepFn>(() => {});
  const playBlockAudioRef = useRef<PlayBlockAudioFn>(async () => {});
  const startAudioPrerenderRef = useRef<StartAudioPrerenderFn>(async () => {});
  const scriptTransitionRef = useRef<ScriptTransitionCallbacks>({
    setIsScriptCompleted: () => {},
    setPendingScriptStart: () => {},
    setIsScriptTransitioning: () => {},
  });

  if (!mytaverseEventRef.current) {
    mytaverseEventRef.current = {} as any;
  }

  // "Ask Me Anything" defaults to enabled; only an explicit false disables it.
  const isAskMeAnythingEnabledRef = useRef(true);
  isAskMeAnythingEnabledRef.current =
    mytaverseEventRef.current?.currentEvent?.settings?.aiAMAEnabled !== false;

  const getEffectiveAiSettings: GetEffectiveAiSettingsFn = () => {
    const mytaverseEvent = mytaverseEventRef.current;
    let eventAiSettings: IEventSettingsAiAssistant | undefined;

    if (mytaverseEvent?.currentEventId) {
      eventAiSettings =
        mytaverseEvent.currentEvent?.settings?.aiAssistant ??
        mytaverseEvent.events?.find(
          (event: any) => event.eventId === mytaverseEvent.currentEventId,
        )?.settings?.aiAssistant;
    } else if (debugEventId) {
      eventAiSettings = debugEventAiSettingsRef.current ?? undefined;
    }

    const language = eventAiSettings?.language || defaultAiSettings.language;
    const baseLanguage = eventAiSettings?.baseLanguage || language;

    return {
      engine: eventAiSettings?.engine || defaultAiSettings.engine,
      voiceId: eventAiSettings?.voiceId || defaultAiSettings.voiceId,
      language,
      baseLanguage,
      languages: eventAiSettings?.languages,
      voicesByLanguage: eventAiSettings?.voicesByLanguage,
    };
  };

  // ---- Hooks (order matters: chat before progress so we can pass its setter) ----

  const navigation = useAssistantNavigation();

  const activeRegionNameRef = useRef<string | undefined>(undefined);

  // chat has no dependencies on other hooks — initialize before progress
  const chat = useAssistantChat({
    mytaverseEventRef,
    activeBrandingIdRef,
    generatePollyAudioRef,
    getEffectiveAiSettings,
    activeRegionNameRef,
  });

  // React state setters are stable references, safe to pass directly to progress
  const progress = useAssistantProgress({
    mytaverseEventRef,
    linearScriptPlayerRef,
    stopPlaybackRef,
    enqueuedScriptIdRef,
    resetAssistantUIRef,
    setAwaitingResponseType: chat.setAwaitingResponseType,
    activeBrandingIdRef,
  });

  const scripts = useAssistantScripts({
    mytaverseEventRef,
    activeBrandingIdRef,
    selectedLanguageRef,
    preRenderedAudioRef,
    debugEventAiSettingsRef,
    userNameRef,
    getEffectiveAiSettings,
  });

  const audio = useAssistantAudio({
    openRef,
    setOpen: setOpenAuto,
    linearScriptPlayerRef,
    enqueuedScriptIdRef,
    scriptsRef: scripts.scriptsRef,
    startFlowRef,
    stopPlaybackRef,
    preRenderedAudioRef,
    scriptTransitionRef,
    pendingRegionClearRef,
    resetAssistantUIRef,
    getEffectiveAiSettings,
  });

  const flow = useScriptFlow({
    scriptsRef: scripts.scriptsRef,
    progressState: progress.progressState,
    sessionProgressRef: progress.sessionProgressRef,
    linearScriptPlayerRef,
    playBlockAudioRef,
    cbOnCompletedStepRef,
    resetAssistantUIRef,
    mytaverseUserRef,
    mytaverseEventRef,
    userNameRef,
    selectedLanguageRef,
    preRenderedAudioRef,
    isAudioPlaybackActiveRef: audio.isAudioPlaybackActiveRef,
    openRef,
    setOpen: setOpenAuto,
    startAudioPrerenderRef,
    startFlowRef,
    getEffectiveAiSettings,
  });

  const region = useRegionHandler({
    linearScriptPlayerRef,
    isAudioPlaybackActiveRef: audio.isAudioPlaybackActiveRef,
    scriptsRef: scripts.scriptsRef,
    progressState: progress.progressState,
    sessionProgressRef: progress.sessionProgressRef,
    enqueuedScriptIdRef,
    pendingRegionClearRef,
    stopPlaybackRef,
    startFlowRef,
    setOpen,
    isAskMeAnythingEnabledRef,
    joinTriggerNameRef,
    parentTriggerMapRef,
    setActiveScreen: navigation.setActiveScreen,
    setNavigationHistory: navigation.setNavigationHistory,
    setIsScriptCompleted: flow.setIsScriptCompleted,
    setIsScriptTransitioning: flow.setIsScriptTransitioning,
    setIsScriptQuizActive: flow.setIsScriptQuizActive,
    setIsDisplayUserFeedback: chat.setIsDisplayUserFeedback,
    setCanUserSkip: flow.setCanUserSkip,
    setPendingScriptStart: flow.setPendingScriptStart,
  });

  // ---- Wire forward-refs every render ----

  stopPlaybackRef.current = audio.stopPlayback;
  startFlowRef.current = flow.startFlow;
  generatePollyAudioRef.current = audio.generatePollyAudio;
  cbOnCompletedStepRef.current = progress.cbOnCompletedStep;
  playBlockAudioRef.current = audio.playBlockAudio;
  startAudioPrerenderRef.current = audio.startAudioPrerender;

  scriptTransitionRef.current = {
    setIsScriptCompleted: flow.setIsScriptCompleted,
    setPendingScriptStart: flow.setPendingScriptStart,
    setIsScriptTransitioning: flow.setIsScriptTransitioning,
  };

  const resetAssistantUI = useCallback(() => {
    globals.activeScreenGlob = SCREENS.DEFAULT;
    navigation.setActiveScreen(SCREENS.DEFAULT);
    navigation.setNavigationHistory([SCREENS.DEFAULT]);
    flow.setIsScriptCompleted(false);
    flow.setIsScriptTransitioning(false);
    flow.setIsScriptQuizActive(false);
    chat.setIsDisplayUserFeedback(false);
    flow.setCanUserSkip(false);
    flow.setPendingScriptStart(null);
  }, []);
  resetAssistantUIRef.current = resetAssistantUI;

  const setIsMuted = useCallback(() => {
    setIsMutedLoc((prev) => {
      globals.isMutedGlob = !prev;
      return !prev;
    });
  }, []);

  const teleportToRegion = useCallback(
    (regionName: string) =>
      teleportToRegionFn(regionName, sendJSONMessageToWebSocketRef),
    [],
  );

  // Editing a script while it's the one currently loaded in the player used to
  // have no visible effect until the user left and re-entered the
  // trigger/spot — fetchScripts() refreshes scriptsRef, but the already-built
  // LinearScriptPlayer keeps running on the stale nodes it was constructed
  // from. If nothing is actively narrating right now (paused, or idle between
  // steps), it's safe to silently rebuild the player from the fresh script
  // data at the same step — audio IS actively playing is the one case left
  // alone, matching the existing "cycle out of the region" behavior.
  //
  // isAudioPlaybackActiveRef is NOT the right signal here: pauseAudioPlayback()
  // (the dedicated Play/Pause control, and the mute button) only pauses the
  // AudioVisualizer — it never touches isAudioPlaybackActiveRef, which stays
  // true until the audio either ends naturally or stopPlayback() runs. Using
  // it here would treat "explicitly paused" as "still playing" and skip the
  // reload — exactly the case this fix is for. globals.visualizerInstance's
  // own isPlaying() reflects whether it's actually emitting sound right now.
  const reloadActiveScriptIfIdle = useCallback(() => {
    const player = linearScriptPlayerRef.current;
    const playerState = player?.getCurrentState();
    const isActuallyPlaying = !!globals.visualizerInstance?.isPlaying?.();

    if (!playerState?.isRunning || isActuallyPlaying || !playerState.name) {
      return;
    }

    const currentIndex = player?.getCurrentIndex() ?? 0;
    const trigger = playerState.name;

    stopPlaybackRef.current();
    player?.stop();
    startFlowRef.current(trigger, {
      startStep: currentIndex + 1,
      bypassPlaybackPolicy: true,
    });
  }, []);

  const fetchScriptsAndReload = useCallback(async () => {
    const newScripts = await scripts.fetchScripts();
    scripts.scriptsRef.current = newScripts;
    reloadActiveScriptIfIdle();
    return newScripts;
  }, [reloadActiveScriptIfIdle]);

  // ---- Effects ----

  useEffect(() => {
    globals.isMutedGlob = false;
    setIsMutedLoc(false);
  }, []);

  useEffect(() => {
    initDebugSendFn((msg) => sendJSONMessageToWebSocketRef.current(msg));
    return () => initDebugSendFn(() => {});
  }, []);

  // Stop any in-progress audio whenever the assistant panel is closed, so a
  // reply that started playing doesn't keep playing after the user closes it
  // (e.g. via the home-bar assistant icon). Covers every close path that flips
  // `open` to false, not just a single button handler.
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      stopPlaybackRef.current();
    }

    prevOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (objectPoiId === null) {
      resetAssistantUI();
      return;
    }

    if (!objectPoiId) {
      return;
    }

    const script = scripts.scriptsRef.current.find(
      (s) =>
        s.triggerType === 'object' &&
        (Array.isArray(s.scriptTrigger)
          ? s.scriptTrigger.includes(objectPoiId)
          : s.scriptTrigger === objectPoiId),
    );

    if (!script) {
      return;
    }

    startFlowRef.current(objectPoiId, { scriptId: script.scriptId });
  }, [objectPoiId, resetAssistantUI]);

  const effectiveRegionName =
    region.simulatedRegionOverride !== undefined
      ? region.simulatedRegionOverride
      : currentRegionName;

  activeRegionNameRef.current = effectiveRegionName;

  useEffect(() => {
    region.handleRegionChange(effectiveRegionName ?? '');
  }, [effectiveRegionName]);

  // Editor Mode's "Teleport to trigger" renders outside this provider's tree
  // (see MytaverseEventProvider.pendingAssistantRegionTrigger), so it can't
  // call triggerRegionChange from context directly. It bumps the nonce here
  // instead; replay the region change exactly once per nonce.
  const lastRegionTriggerNonceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (
      !pendingAssistantRegionTrigger ||
      pendingAssistantRegionTrigger.nonce === lastRegionTriggerNonceRef.current
    ) {
      return;
    }

    lastRegionTriggerNonceRef.current = pendingAssistantRegionTrigger.nonce;
    log('[region] replaying pendingAssistantRegionTrigger', {
      trigger: pendingAssistantRegionTrigger.trigger,
      nonce: pendingAssistantRegionTrigger.nonce,
    });
    region.handleRegionChange(pendingAssistantRegionTrigger.trigger);
  }, [pendingAssistantRegionTrigger]);

  useAsyncEffect(async () => {
    await Promise.all([
      scripts.fetchScripts(),
      progress.fetchParticipantProgress(),
    ]);
  }, []);

  useEffect(() => {
    if (!assistantScriptUpdateTick) {
      return;
    }

    audio.clearPrerenderedAudio();
    fetchScriptsAndReload();
  }, [assistantScriptUpdateTick, fetchScriptsAndReload]);

  useEffect(() => {
    if (prevActiveBrandingIdRef.current === activeBrandingId) {
      return;
    }

    prevActiveBrandingIdRef.current = activeBrandingId;
    resetAssistantUI();
    progress.sessionProgressRef.current = {};
    // Clear before refetching — fetchParticipantProgress merges progressState.current
    // in as "local unsynced progress", and leftover data from the old branding
    // would otherwise leak into the newly loaded branding's progress.
    progress.progressState.current = '{}';
    audio.clearPrerenderedAudio();

    void (async () => {
      const [newScripts] = await Promise.all([
        scripts.fetchScripts(),
        // Progress is scoped by activeBrandingId — without this, progressState
        // (and anything reading it, e.g. the Visited Areas pill) keeps showing
        // whichever branding was active when it was last loaded.
        progress.fetchParticipantProgress(),
      ]);
      scripts.scriptsRef.current = newScripts;
      region.handleRegionChange(effectiveRegionName ?? '');
    })();
  }, [activeBrandingId, resetAssistantUI]);

  // ---- Derived values ----

  const currentTag = effectiveRegionName || undefined;

  const filteredChatHistory = useMemo(() => {
    return chat.chatHistory.filter((m) => {
      if (activeBrandingId && m.brandingId !== activeBrandingId) {
        return false;
      }
      if (m.tag !== currentTag) {
        return false;
      }
      return true;
    });
  }, [chat.chatHistory, activeBrandingId, currentTag]);

  // ---- Context value ----

  const contextValue = useMemo(
    () => ({
      activeScreen: navigation.activeScreen,
      setActiveScreen: navigation.setActiveScreen,
      open,
      setOpen,
      handleSendRequest: chat.handleSendRequest,
      chatHistory: filteredChatHistory,
      navigate: navigation.navigate,
      setNavigationHistory: navigation.setNavigationHistory,
      awaitingResponseType: chat.awaitingResponseType,
      stopPlayback: audio.stopPlayback,
      isRecording,
      setIsRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      startFlow: flow.startFlow,
      simulateRegionChange: region.simulateRegionChange,
      triggerRegionChange: region.handleRegionChange,
      linearScriptPlayerRef,
      isAudioPlaybackActiveRef: audio.isAudioPlaybackActiveRef,
      isAudioPlaybackActive: audio.isAudioPlaybackActive,
      scripts: scripts.scripts,
      isDisplayUserFeedback: chat.isDisplayUserFeedback,
      setIsDisplayUserFeedback: chat.setIsDisplayUserFeedback,
      handleSubmitUserFeedback: chat.handleSubmitUserFeedback,
      fetchScripts: fetchScriptsAndReload,
      fetchParticipantProgress: progress.fetchParticipantProgress,
      resetProgress: progress.resetProgress,
      saveQuizAnswers: progress.saveQuizAnswers,
      clearPrerenderedAudio: audio.clearPrerenderedAudio,
      isScriptQuizActive: flow.isScriptQuizActive,
      setIsScriptQuizActive: flow.setIsScriptQuizActive,
      audioEndTick: audio.audioEndTick,
      isScriptCompleted: flow.isScriptCompleted,
      setIsScriptCompleted: flow.setIsScriptCompleted,
      isScriptTransitioning: flow.isScriptTransitioning,
      canUserSkip: flow.canUserSkip,
      pendingScriptStart: flow.pendingScriptStart,
      confirmScriptStart: flow.confirmScriptStart,
      resetAssistantUI,
      userName: userName || '',
      activeRegionName: effectiveRegionName ?? '',
      currentMapName:
        mytaverseEventRef.current?.currentRoom?.map?.name || undefined,
      joinTriggerName,
      activeBrandingId:
        mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
        activeBrandingId ||
        undefined,
      availableLanguages:
        getEffectiveAiSettings().languages ?? defaultAvailableLanguages,
      audioLanguage,
      setAudioLanguage,
      isPollyAudioLoading: audio.isPollyAudioLoading,
      spotTriggerType,
      setSpotTriggerType,
      isSpotImageModalOpen,
      setIsSpotImageModalOpen,
      setPlaybackRate: audio.setPlaybackRate,
      getAudioProgress: audio.getAudioProgress,
      pauseAudioPlayback: audio.pauseAudioPlayback,
      resumeAudioPlayback: audio.resumeAudioPlayback,
      progressStateRef: progress.progressState,
    }),
    [
      navigation.activeScreen,
      open,
      chat.handleSendRequest,
      filteredChatHistory,
      navigation.navigate,
      chat.awaitingResponseType,
      audio.stopPlayback,
      isRecording,
      audioPromptHistory,
      isMuted,
      setIsMuted,
      teleportToRegion,
      flow.startFlow,
      region.simulateRegionChange,
      region.handleRegionChange,
      audio.isAudioPlaybackActive,
      scripts.scripts,
      chat.isDisplayUserFeedback,
      chat.handleSubmitUserFeedback,
      fetchScriptsAndReload,
      progress.fetchParticipantProgress,
      progress.resetProgress,
      progress.saveQuizAnswers,
      audio.clearPrerenderedAudio,
      flow.isScriptQuizActive,
      audio.audioEndTick,
      flow.isScriptCompleted,
      flow.isScriptTransitioning,
      flow.canUserSkip,
      flow.pendingScriptStart,
      flow.confirmScriptStart,
      resetAssistantUI,
      userName,
      effectiveRegionName,
      audioLanguage,
      audio.isPollyAudioLoading,
      spotTriggerType,
      isSpotImageModalOpen,
      joinTriggerName,
      audio.setPlaybackRate,
      audio.getAudioProgress,
      audio.pauseAudioPlayback,
      audio.resumeAudioPlayback,
    ],
  );

  return (
    <AssistantContext.Provider value={contextValue}>
      {children}
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = memo(AssistantProviderInner);
